/**
 * @file usePickMarkForm.ts
 * @description Состояние ввода количества в панели «Отметить позицию»
 * (phase19.24 этап 1, шаг 1; логика вынесена из `PickMarkForm`).
 *
 * Хук появился, когда кнопка «Отметить» уехала в `footer` общей панели
 * `DocumentAside`: поля и кнопка теперь в разных слотах, а состояние у них
 * одно. Держать его в двух местах — верный способ развести валидацию и
 * отправку.
 *
 * Перебор плана ловим ДО запроса: сервер ответит 422, но кладовщику нужен
 * ответ сразу и словами из §3.5 («По позиции уже собрано 3 / 3 шт»).
 */

import { useState } from "react";

import type { PickingTaskItemDto } from "../../../lib/api-picking";
import { formatItemQty } from "../../../lib/picking-format";
import { validateQty } from "../../../lib/qty-input";

export interface PickMarkFormState {
  qty: string;
  setQty: (next: string) => void;
  /** Текст ошибки под кнопкой; null — всё в порядке. */
  error: string | null;
  /** Отметить выбранную строку введённым количеством. */
  mark: () => void;
}

export function usePickMarkForm(
  /** Выбранная строка задания; null — ещё не выбрана. */
  item: PickingTaskItemDto | null,
  /** Итоговое собранное количество (собрано + введённое). */
  onMark: (itemId: string, qtyPicked: number) => void,
): PickMarkFormState {
  const [qty, setQtyValue] = useState("1");
  const [error, setError] = useState<string | null>(null);

  function setQty(next: string) {
    setQtyValue(next);
    setError(null);
  }

  function mark() {
    if (!item) {
      setError("Выберите позицию: отсканируйте артикул или нажмите «Подбор».");
      return;
    }
    // Остаток по строке — сколько ещё можно добавить к собранному.
    const left = Number(item.qtyPlanned) - Number(item.qtyPicked);
    const message = validateQty(qty, item.unit, { min: 0, allowZero: false });
    if (message) {
      setError(message);
      return;
    }
    const add = Number(qty.replace(",", "."));
    if (add > left) {
      setError(
        `По позиции уже собрано ${formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}`,
      );
      return;
    }
    setError(null);
    onMark(item.id, Number(item.qtyPicked) + add);
    setQtyValue("1");
  }

  return { qty, setQty, error, mark };
}
